import { type ReactNode } from 'react';

import {
  Alert,
  Button,
  LabelChip,
  LayoutCard,
  MetricsBox,
  Panel,
  Stack,
  Typography,
  tokens,
} from '@cast/design-system';
import type { Dayjs } from 'dayjs';

import {
  FREE_VCPU_CAP,
  isOverPlanCap,
  isSoftOverage,
  type ReportingPlan,
  type ReportingPlanId,
} from './reportingPlans';
import {
  formatReportingTrialEndShort,
  formatReportingVcpu,
  formatReportingVcpuCapHint,
} from './reportingFormat';
import { ReportingPlanHardUpgradeBanner } from './ReportingPlanHardUpgradeBanner';
import { ReportingPlanSoftUpgradeBanner } from './ReportingPlanSoftUpgradeBanner';
import { useReportingPlansWithPrices } from './useReportingPlansWithPrices';
import {
  doesReportingMonetizationApply,
  useReportingTrial,
} from './useReportingTrial';

type Metric = {
  label: string;
  value: ReactNode;
  unit?: ReactNode;
  footer?: ReactNode;
  /** Defaults to P18B — metric* is 900 (too heavy); P18M is 500 (too light). */
  valueVariant?: 'metricmd' | 'P18M' | 'P18B';
};

type ReportingPlatformUsageSummaryProps = {
  avgProvisionedVcpu?: number;
  clusterCount?: number;
  /** When BE returns current paid plan after Stripe webhook. */
  currentPaidPlanId?: Exclude<ReportingPlanId, 'free' | 'enterprise'>;
  /** Stripe current period end — shown as Next payment. */
  nextPaymentAt?: Dayjs;
  /**
   * Fleet/overview stats still loading (or failed). Shows placeholders for
   * post-load values and suppresses recommendation/entitlement messaging.
   */
  isLoading?: boolean;
  onViewPlans?: () => void;
  onChangePlan?: () => void;
  onManageBilling?: () => void;
  /** Soft/hard upgrade CTAs → plans. */
  onUpgrade?: () => void;
};

const MetricFooter = ({ children }: { children: ReactNode }) => (
  <Typography variant="P12R" color={tokens.theme.fg.structural.secondary}>
    {children}
  </Typography>
);

/** `$249 / mo` → `{ value: '$249', unit: '/mo' }`. */
const splitPlanPrice = (
  priceLabel: string
): { value: string; unit?: string } => {
  const match = priceLabel.trim().match(/^(\$\d[\d,]*)\s*\/\s*mo$/i);
  if (!match) {
    return { value: priceLabel };
  }
  return { value: match[1], unit: '/mo' };
};

const MetricPanel = ({
  label,
  value,
  unit,
  footer,
  valueVariant = 'P18B',
}: Metric) => (
  <Panel
    sx={{
      p: 16,
      flex: '1 1 140px',
      minWidth: 140,
    }}
  >
    <MetricsBox
      size="m"
      slots={{
        label,
        value,
        unit,
        footer: footer ? <MetricFooter>{footer}</MetricFooter> : undefined,
      }}
      slotProps={{
        label: {
          color: tokens.theme.fg.structural.secondary,
          sx: { textTransform: 'uppercase' },
        },
        value: {
          variant: valueVariant,
          color: tokens.theme.fg.structural.primary,
          component:
            typeof value === 'string' || typeof value === 'number'
              ? undefined
              : 'div',
        },
        unit: {
          sx: { textTransform: 'uppercase' },
        },
      }}
    />
  </Panel>
);

const SummaryShell = ({
  actions,
  metrics,
  banner,
}: {
  actions?: ReactNode;
  metrics: Metric[];
  banner?: ReactNode;
}) => (
  <LayoutCard
    title="Summary"
    headerEndContent={actions}
    testId="reporting-platform-usage-summary"
    slotProps={{
      title: { variant: 'P18B' },
      header: { py: 16, sx: { gap: '16px' } },
      content: { sx: { pt: 0 } },
    }}
  >
    <Stack gap={16}>
      <Stack direction="row" gap={12} flexWrap="wrap" alignItems="stretch">
        {metrics.map((metric) => (
          <MetricPanel key={metric.label} {...metric} />
        ))}
      </Stack>
      {banner}
    </Stack>
  </LayoutCard>
);

const resolvePaidPlan = ({
  plans,
  currentPaidPlanId,
  avgProvisionedVcpu,
  recommendPlan,
}: {
  plans: ReportingPlan[];
  currentPaidPlanId: ReportingPlatformUsageSummaryProps['currentPaidPlanId'];
  avgProvisionedVcpu: number;
  recommendPlan: (avg: number) => ReportingPlan;
}) => {
  const starter = plans.find((plan) => plan.id === 'starter') ?? plans[1];
  if (currentPaidPlanId) {
    return plans.find((plan) => plan.id === currentPaidPlanId) ?? starter;
  }
  const recommended = recommendPlan(avgProvisionedVcpu);
  if (recommended.id !== 'free' && recommended.id !== 'enterprise') {
    return recommended;
  }
  return starter;
};

/**
 * Platform usage → Reporting summary (Figma 27:37702 / trial variants):
 * LayoutCard + Panel + MetricsBox tiles.
 */
export const ReportingPlatformUsageSummary = ({
  avgProvisionedVcpu = 0,
  clusterCount,
  currentPaidPlanId,
  nextPaymentAt,
  isLoading = false,
  onViewPlans,
  onChangePlan,
  onManageBilling,
  onUpgrade,
}: ReportingPlatformUsageSummaryProps) => {
  const trial = useReportingTrial();
  const { state, expiresAt, expiresInDays, isEligible } = trial;
  const { plans, recommendPlan } = useReportingPlansWithPrices();

  if (!doesReportingMonetizationApply(trial)) {
    return null;
  }

  const recommended = recommendPlan(avgProvisionedVcpu);
  const trialEndsOn = formatReportingTrialEndShort(expiresAt, expiresInDays);
  const roundedUsage = formatReportingVcpu(avgProvisionedVcpu);
  const fitsFree = avgProvisionedVcpu <= FREE_VCPU_CAP;
  const clusterHint =
    clusterCount != null ? `${clusterCount} clusters` : undefined;
  const recommendedHint = formatReportingVcpuCapHint(
    recommended.vcpuCap,
    recommended.priceLabel
  );
  const nextPaymentLabel = nextPaymentAt?.isValid()
    ? nextPaymentAt.format('D MMMM YYYY')
    : '—';

  // Recommendation content is not meaningful until usage data lands.
  const minimumPlanMetrics: Metric[] = isLoading
    ? []
    : [
        {
          label: 'Minimum plan for your fleet',
          value: recommended.name,
          footer: recommendedHint,
        },
      ];

  if (state === 'paid' || currentPaidPlanId) {
    const current = resolvePaidPlan({
      plans,
      currentPaidPlanId,
      avgProvisionedVcpu,
      recommendPlan,
    });
    const cap = current.vcpuCap ?? 0;
    const price = splitPlanPrice(current.priceLabel);
    const hardOverage = isSoftOverage(avgProvisionedVcpu, cap);
    const softOverage = !hardOverage && isOverPlanCap(avgProvisionedVcpu, cap);

    return (
      <SummaryShell
        actions={
          <Stack direction="row" gap={16}>
            <Button
              variant="secondary"
              size="sm"
              onClick={onManageBilling}
              testId="reporting-usage-manage-billing"
            >
              Manage billing
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={onChangePlan ?? onViewPlans}
              testId="reporting-usage-change-plan"
            >
              Change plan
            </Button>
          </Stack>
        }
        metrics={[
          { label: 'Current plan', value: isLoading ? '—' : current.name },
          {
            label: 'Plan price',
            value: isLoading ? '—' : price.value,
            unit: isLoading ? undefined : price.unit,
          },
          {
            label: 'Current usage',
            value: isLoading
              ? '—'
              : `${roundedUsage} / ${formatReportingVcpu(cap)}`,
            unit: isLoading ? undefined : 'vCPU',
          },
          {
            label: 'Next payment',
            value: isLoading ? '—' : nextPaymentLabel,
          },
        ]}
        banner={
          isLoading ? undefined : hardOverage ? (
            <ReportingPlanHardUpgradeBanner
              planId={current.id}
              planName={current.name}
              planVcpuCap={cap}
              avgProvisionedVcpu={avgProvisionedVcpu}
              onUpgrade={onUpgrade ?? onViewPlans}
            />
          ) : softOverage ? (
            <ReportingPlanSoftUpgradeBanner
              planId={current.id}
              planName={current.name}
              planVcpuCap={cap}
              avgProvisionedVcpu={avgProvisionedVcpu}
              onUpgrade={onUpgrade ?? onViewPlans}
            />
          ) : undefined
        }
      />
    );
  }

  if (state === 'active') {
    return (
      <SummaryShell
        actions={
          <Button
            variant="primary"
            size="sm"
            onClick={onViewPlans}
            testId="reporting-usage-view-plans"
          >
            View plans
          </Button>
        }
        metrics={[
          {
            label: 'Current plan',
            value: 'Trial',
            footer: 'Full access for 30 days',
          },
          {
            label: 'Trial ends',
            value: trialEndsOn ?? '—',
            footer: 'No automatic charge',
          },
          {
            label: 'Average usage',
            value: isLoading ? '—' : roundedUsage,
            unit: isLoading ? undefined : 'vCPU',
            footer: 'Provisioned · This billing period',
          },
          ...minimumPlanMetrics,
        ]}
      />
    );
  }

  if (state === 'grace-period' || state === 'post-trial') {
    if (fitsFree) {
      const freePrice = splitPlanPrice(
        plans.find((plan) => plan.id === 'free')!.priceLabel
      );

      return (
        <SummaryShell
          actions={
            <Button
              variant="primary"
              size="sm"
              onClick={onChangePlan ?? onViewPlans}
              testId="reporting-usage-change-plan"
            >
              Change plan
            </Button>
          }
          metrics={[
            { label: 'Current plan', value: isLoading ? '—' : 'Free' },
            {
              label: 'Plan price',
              value: isLoading ? '—' : freePrice.value,
              unit: isLoading ? undefined : freePrice.unit,
            },
            {
              label: 'Current usage',
              value: isLoading
                ? '—'
                : `${roundedUsage} / ${formatReportingVcpu(FREE_VCPU_CAP)}`,
              unit: isLoading ? undefined : 'vCPU',
            },
          ]}
          banner={
            isLoading ? undefined : (
              <Alert
                color="info"
                size="sm"
                testId="reporting-usage-free-banner"
              >
                <Typography variant="P14B" component="span">
                  Your trial has ended. You&apos;re now on Free.{' '}
                </Typography>
                <Typography variant="P14R" component="span">
                  Reporting continues for all your clusters with 30-day history
                  and hourly resolution. Paid capabilities are no longer
                  available.
                </Typography>
              </Alert>
            )
          }
        />
      );
    }

    // Over Free after trial — Figma shows paused collection; MVP still collects,
    // but entitlement is Free-capped until they pay or shrink fleet.
    return (
      <SummaryShell
        actions={
          <Button
            variant="primary"
            size="sm"
            onClick={onViewPlans}
            testId="reporting-usage-view-plans"
          >
            View plans
          </Button>
        }
        metrics={[
          {
            label: 'Current plan',
            value: (
              <Stack direction="row" gap={8} alignItems="center">
                <Typography
                  variant="metricmd"
                  color={tokens.theme.fg.structural.primary}
                >
                  Trial
                </Typography>
                <LabelChip
                  size="sm"
                  sx={{
                    bgcolor: tokens.theme.bg.status.errorSubtle,
                    color: tokens.theme.fg.status.onErrorSubtle,
                    textTransform: 'uppercase',
                  }}
                >
                  Ended
                </LabelChip>
              </Stack>
            ),
          },
          {
            label: 'Trial ended',
            value: trialEndsOn ?? '—',
            footer: 'No payment taken',
          },
          {
            label: 'Average usage',
            value: isLoading ? '—' : roundedUsage,
            unit: isLoading ? undefined : 'vCPU',
            footer: clusterHint
              ? `At trial end · ${clusterHint}`
              : 'At trial end',
          },
          ...minimumPlanMetrics,
        ]}
        banner={
          isLoading ? undefined : (
            <Alert
              color="error"
              size="sm"
              testId="reporting-usage-ended-banner"
            >
              <Stack
                direction="row"
                gap={12}
                alignItems="center"
                flexWrap="wrap"
                width="100%"
              >
                <Typography variant="P14R" flex={1}>
                  <Typography variant="P14B" component="span">
                    Your trial has ended.{' '}
                  </Typography>
                  Choose a paid plan, or disconnect clusters totaling up to{' '}
                  {FREE_VCPU_CAP} average vCPUs to continue on Free with basic
                  reporting.
                </Typography>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={onViewPlans}
                  testId="reporting-usage-choose-plan"
                >
                  Choose a plan
                </Button>
              </Stack>
            </Alert>
          )
        }
      />
    );
  }

  // Eligible, trial not started yet
  if (isEligible && state === 'pre-trial') {
    return (
      <SummaryShell
        actions={
          <Button
            variant="primary"
            size="sm"
            onClick={onViewPlans}
            testId="reporting-usage-view-plans"
          >
            View plans
          </Button>
        }
        metrics={[
          {
            label: 'Current plan',
            value: 'Eligible',
            footer: 'Connect a cluster to start your 30-day trial',
          },
          {
            label: 'Average usage',
            value: isLoading ? '—' : roundedUsage,
            unit: isLoading ? undefined : 'vCPU',
            footer: 'Provisioned · Connected clusters',
          },
          ...minimumPlanMetrics,
        ]}
      />
    );
  }

  return null;
};
